import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../contexts/LanguageContext';
import { 
  DocumentArrowDownIcon, 
  MagnifyingGlassIcon, 
  ChevronDownIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { fetchWithdrawals, deleteWithdrawal } from '../../redux/slices/withdrawalsSlice';
import Pagination from '../Common/Pagination';
import ConfirmationModal from '../Common/ConfirmationModal';
import WithdrawalStatusModal from './WithdrawalStatusModal';

const AllWithdrawals = () => {
  const dispatch = useDispatch();
  const { t, tStatus } = useTranslation();
  const { withdrawals, total, totalPages, currentPage, loading, error } = useSelector(state => state.withdrawals);

  const [searchParams] = useSearchParams();
  const statusTab = (searchParams.get('status') || 'all').toLowerCase();
  const byStatus = statusTab === 'all' ? '' : statusTab;

  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [withdrawalToDelete, setWithdrawalToDelete] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    dispatch(fetchWithdrawals({ currentPage: 1, limit, byStatus }));
  }, [dispatch, limit, byStatus]);

  const handlePageChange = (newPage) => {
    dispatch(fetchWithdrawals({ currentPage: newPage, limit, byStatus }));
  };

  const handleEditStatus = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowStatusModal(true);
  };

  const handleStatusUpdateSuccess = () => {
    dispatch(fetchWithdrawals({ currentPage, limit }));
  };

  const handleViewDetails = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowDetailsModal(true);
  };

  const handleDeleteClick = (withdrawal) => {
    setWithdrawalToDelete(withdrawal);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (withdrawalToDelete) {
      await dispatch(deleteWithdrawal(withdrawalToDelete._id));
      setShowDeleteModal(false);
      setWithdrawalToDelete(null);
      dispatch(fetchWithdrawals({ currentPage, limit }));
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDotColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-500';
      case 'processing':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'rejected':
        return 'bg-red-500';
      case 'cancelled':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleReceiptDownload = (receiptUrl) => {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-dashboardCoral"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-600">Error: {error}</p>
        <button
          onClick={() => dispatch(fetchWithdrawals({ currentPage, limit }))}
          className="mt-2 px-4 py-2 bg-dashboardCoral text-white rounded hover:bg-opacity-90"
        >
          {t("common.retry")}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
      {/* Dark Header */}
      <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-white font-semibold text-lg">{t("withdrawals.title")}</h2>
          <span className="text-white font-semibold">({total})</span>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
            <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
            <input
              type="text"
              placeholder={t("common.search")}
              className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-28"
            />
          </div>
          
          <button className="flex items-center gap-2 px-3 py-1.5 border border-white rounded text-white text-sm font-light">
            <span>{t("common.sortBy")}</span>
            <ChevronDownIcon className="w-[18px] h-[18px] text-themeLightGray" />
          </button>
          
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="bg-transparent border border-white rounded px-3 py-1.5 text-white text-sm font-light outline-none"
          >
            <option value={10} className="text-dashboardDark">10</option>
            <option value={25} className="text-dashboardDark">25</option>
            <option value={50} className="text-dashboardDark">50</option>
            <option value={100} className="text-dashboardDark">100</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dashboardStroke">
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.srNo")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.user")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.amount")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.status")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.type")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("withdrawals.col.walletBalance")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.createdAt")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("withdrawals.col.receipt")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-medium text-dashboardText">{t("col.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {(byStatus
              ? withdrawals.filter((w) => (w.status || '').toLowerCase() === byStatus)
              : withdrawals
            ).map((withdrawal, index) => (
              <tr 
                key={withdrawal._id}
                className={`border-b border-dashboardStroke hover:bg-gray-50 transition-colors ${
                  index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                }`}
              >
                <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                  {(currentPage - 1) * limit + index + 1}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-base font-medium text-dashboardDarkText">{withdrawal.userId?.name || 'N/A'}</div>
                  <div className="text-sm text-gray-500">{withdrawal.userId?.email || 'N/A'}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-base font-semibold text-dashboardCoral">
                  ${withdrawal.amount}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(withdrawal.status)}`}>
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${getStatusDotColor(withdrawal.status)}`} />
                    {tStatus(withdrawal.status || 'pending')}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                  {withdrawal.type || 'N/A'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                  ${withdrawal.userId?.walletBalance || 0}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                  {formatDate(withdrawal.createdAt)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {withdrawal.receiptUrl ? (
                    <button
                      onClick={() => handleReceiptDownload(withdrawal.receiptUrl)}
                      className="text-dashboardCoral hover:text-opacity-80"
                      title="Download Receipt"
                    >
                      <DocumentArrowDownIcon className="w-6 h-6" />
                    </button>
                  ) : (
                    <span className="text-gray-400 text-sm">{t("common.noReceipt")}</span>
                  )}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-0.5">
                    <button 
                      onClick={() => handleViewDetails(withdrawal)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                    </button>
                    <div className="w-px h-6 bg-dashboardStroke"></div>
                    <button 
                      onClick={() => handleEditStatus(withdrawal)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <PencilIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                    </button>
                    <div className="w-px h-6 bg-dashboardStroke"></div>
                    <button 
                      onClick={() => handleDeleteClick(withdrawal)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <TrashIcon className="w-6 h-6 text-dashboardCoral" strokeWidth={1.3} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-4 py-4 border-t border-dashboardStroke">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Status Update Modal */}
      <WithdrawalStatusModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedWithdrawal(null);
        }}
        withdrawal={selectedWithdrawal}
        onStatusUpdateSuccess={handleStatusUpdateSuccess}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t("deleteConfirm.withdrawal.title")}
        message={t("deleteConfirm.withdrawal.message").replace("{name}", withdrawalToDelete?.userId?.name || "")}
        confirmText={t("deleteConfirm.delete")}
        cancelText={t("deleteConfirm.cancel")}
      />

      {/* Details Modal */}
      {showDetailsModal && selectedWithdrawal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{t("withdrawals.details.title")}</h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold mb-2">{t("withdrawals.details.basicInfo")}</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>{t("withdrawals.details.amount")}:</strong> ${selectedWithdrawal.amount}</p>
                  <p><strong>{t("col.status")}:</strong>
                    <span className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(selectedWithdrawal.status)}`}>
                      {tStatus(selectedWithdrawal.status)}
                    </span>
                  </p>
                  <p><strong>{t("withdrawals.details.type")}:</strong> {selectedWithdrawal.type}</p>
                  <p><strong>{t("withdrawals.details.created")}:</strong> {formatDate(selectedWithdrawal.createdAt)}</p>
                  <p><strong>{t("withdrawals.details.updated")}:</strong> {formatDate(selectedWithdrawal.updatedAt)}</p>
                  {selectedWithdrawal.remarks && (
                    <p><strong>{t("withdrawals.details.remarks")}:</strong> {selectedWithdrawal.remarks}</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">{t("withdrawals.details.userInfo")}</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>{t("withdrawals.details.name")}:</strong> {selectedWithdrawal.userId?.name}</p>
                  <p><strong>{t("col.email")}:</strong> {selectedWithdrawal.userId?.email}</p>
                  <p><strong>{t("col.phone")}:</strong> {selectedWithdrawal.userId?.phone || 'N/A'}</p>
                  <p><strong>{t("withdrawals.details.walletBalance")}:</strong> ${selectedWithdrawal.userId?.walletBalance}</p>
                  <p><strong>{t("withdrawals.details.verificationStatus")}:</strong> {tStatus(selectedWithdrawal.userId?.identityVerification?.status)}</p>
                </div>
              </div>

              {selectedWithdrawal.userId?.bankDetails && (
                <div className="md:col-span-2">
                  <h3 className="font-semibold mb-2">{t("withdrawals.details.bankDetails")}</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p><strong>{t("withdrawals.details.bankName")}:</strong> {selectedWithdrawal.userId.bankDetails.bankName}</p>
                      <p><strong>{t("withdrawals.details.accountTitle")}:</strong> {selectedWithdrawal.userId.bankDetails.accountTitle}</p>
                      <p><strong>{t("withdrawals.details.accountId")}:</strong> {selectedWithdrawal.userId.bankDetails.accountId}</p>
                    </div>
                    <div>
                      <p><strong>{t("withdrawals.details.iban")}:</strong> {selectedWithdrawal.userId.bankDetails.iban}</p>
                      <p><strong>{t("withdrawals.details.swiftCode")}:</strong> {selectedWithdrawal.userId.bankDetails.swiftcode}</p>
                      <p><strong>{t("withdrawals.details.branchCode")}:</strong> {selectedWithdrawal.userId.bankDetails.branchcode}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300"
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllWithdrawals;